import React from 'react';
import ViewerStore from '../../stores/ViewerStore';
import * as Inovx from '@shared';
import ViewerActions from '../../actions/ViewerActions';
import { BeforeCloseEventArgs, DialogComponent } from '@syncfusion/ej2-react-popups';
import { ComboBoxComponent, FilteringEventArgs } from '@syncfusion/ej2-react-dropdowns';
import FacilityStore from '../../stores/FacilityStore';
import AssetActions from '../../actions/AssetActions';
import useAsyncActionLock from '@shared/hooks/useAsyncActionLock';
const { useState, useEffect, useRef } = React;

var InovxControls = Inovx.Shared.Controls;
var UrlUtilities = Inovx.Shared.Common.UrlUtilities;
var SystemActions = Inovx.Shared.System.Actions;
var CommonMessages = Inovx.Shared.Common.CommonMessages;

interface AssetTagsFormProps {
    //displayMiniMap: boolean;
    //editingAssetTags: IAnnotation;
    onClose?: () => void;
}
interface IAssetWithTags extends IAssetIdentity {
    Tags: IAssetTag[];
}

function getEditedAssetTagCopy() {
    return ViewerStore.getEditedAssetTag() ? { ...ViewerStore.getEditedAssetTag() } : null;
}

const AssetTagsForm = React.forwardRef((props: AssetTagsFormProps, ref) => {
    const [hasAssetTagChange, setHasAssetTagChange] = useState<boolean>(false);
    const [editingTag, setEditingTag] = useState<IAssetTag>(getEditedAssetTagCopy());
    const [isEditAssetModalOpen, setIsEditAssetModalOpen] = useState<boolean>(false);
    const [validation, setValidation] = useState<IValidationDictionaryObject>();
    const [facility, setFacility] = useState<IFacilityDetail>(FacilityStore.getSelectedFacility())
    const [forceRender, setForceRender] = useState(0);
    const [assetTags, setAssetTags] = useState<IAssetWithTags[]>([]);
    const [assetIdentities, setAssetIdentities] = useState<string[]>([]);
    // const [tagName, setTagName] = useState('');
    const editedTagSourceRef = useRef<IAssetTag | null>(ViewerStore.getEditedAssetTag());
    const editingTagRef = useRef<IAssetTag | null>(editingTag);
    const hasAssetTagChangeRef = useRef<boolean>(hasAssetTagChange);
    const assetClassSelectRef = useRef<HTMLSelectElement | null>(null);
    const comboBoxRef = useRef<ComboBoxComponent | null>(null);

    const forceUpdate = () => {
        setForceRender(prev => prev + 1); // Increment state to trigger re-render
    };

    useEffect(() => {
        const handleFacilityStoreChange = () => {
            updateFacilityState();
        };
        const handleViewerStoreChange = () => {
            updateViewerState();
        };

        FacilityStore.addChangeListener(handleFacilityStoreChange);
        ViewerStore.addChangeListener(handleViewerStoreChange);
        return () => {
            FacilityStore.removeChangeListener(handleFacilityStoreChange);
            ViewerStore.removeChangeListener(handleViewerStoreChange);
        }
    }, [])

    useEffect(() => {
        if (editingTag) {
            _initalizeValidation();
        } else {
            setIsEditAssetModalOpen(false);
        }
    }, [])

    useEffect(() => {
        editingTagRef.current = editingTag;
    }, [editingTag])

    useEffect(() => {
        hasAssetTagChangeRef.current = hasAssetTagChange;
    }, [hasAssetTagChange])

    useEffect(() => {
        if (!editingTag?.AssetClass?.ID) {
            setAssetIdentities([]);
            return;
        }

        updateAssetIdentities();
    }, [editingTag?.AssetClass?.ID, facility])

    useEffect(() => {
        const assetClassSelect = assetClassSelectRef.current;

        if (!assetClassSelect)
            return;

        const handleNativeAssetClassChange = (event: Event) => {
            onAssetClassChange((event.target as HTMLSelectElement).value);
        };

        assetClassSelect.addEventListener('change', handleNativeAssetClassChange);

        return () => {
            assetClassSelect.removeEventListener('change', handleNativeAssetClassChange);
        };
    }, [editingTag?.ID, isEditAssetModalOpen, facility])

    useEffect(() => {
        const inputElement = (comboBoxRef.current as any)?.inputElement as HTMLInputElement | undefined;
        const inputGroupElement = inputElement?.closest('.e-input-group') as HTMLElement | null;

        if (!inputElement)
            return;

        const handleNativeTagTextInput = (event: Event) => {
            updateTagText((event.target as HTMLInputElement).value);
        };

        const handleInputGroupClick = (event: Event) => {
            const target = event.target as HTMLElement | null;

            if (!target?.closest('.e-clear-icon'))
                return;

            window.requestAnimationFrame(() => updateTagText(getCurrentTagText()));
        };

        inputElement.addEventListener('input', handleNativeTagTextInput);
        inputGroupElement?.addEventListener('click', handleInputGroupClick);

        return () => {
            inputElement.removeEventListener('input', handleNativeTagTextInput);
            inputGroupElement?.removeEventListener('click', handleInputGroupClick);
        };
    }, [editingTag?.ID, isEditAssetModalOpen])

    const updateFacilityState = () => {
        setFacility(FacilityStore.getSelectedFacility());
    }

    const isSameEditedTag = (left: IAssetTag | null, right: IAssetTag | null) => {
        if (!left || !right)
            return left === right;

        if (left.ID || right.ID)
            return left.ID === right.ID;

        return left.AssetID === right.AssetID && left.View360ID === right.View360ID;
    }

    const updateViewerState = () => {
        const storeTag = ViewerStore.getEditedAssetTag();
        const localEditingTag = editingTagRef.current;

        if (storeTag === editedTagSourceRef.current && isSameEditedTag(storeTag, localEditingTag))
            return;

        if (hasAssetTagChangeRef.current && storeTag && localEditingTag && isSameEditedTag(storeTag, localEditingTag)) {
            editedTagSourceRef.current = storeTag;
            return;
        }

        editedTagSourceRef.current = storeTag;

        if (storeTag) {
            setEditingTag({ ...storeTag });
            _initalizeValidation();
        } else {
            setEditingTag(null);
            setIsEditAssetModalOpen(false);
        }
    }

    const updateAssetArray = () => {
        let gridArray = AssetActions.createAssetTagsTree(facility);
        setAssetTags(gridArray)
    }

    const _initalizeValidation = () => {
        updateAssetArray();
        setValidation({
            Text: {
                invalidInput: false,
                invalidValue: false
            }
        });
        setIsEditAssetModalOpen(true);
    }

    const handleBeforeClose = (arg: BeforeCloseEventArgs) => {
        if (hasAssetTagChange) {
            arg.cancel = true;
            close();
        } else {
            close();
        }
    }

    const close = () => {
        if (hasAssetTagChange) {
            SystemActions.displaySaveChangesDialog({
                message: InovxLoader.t("common:msgBox.saveChanges"),
                confirmCallback: editingTag.ID ? saveAssetTag : createAssetTag,
                cancelCallback: closeModal
            });
        } else {
            closeModal()
        }
    }

    const closeModal = () => {
        editedTagSourceRef.current = null;
        setEditingTag(null);
        ViewerActions.editAssetTag(null);
        setHasAssetTagChange(false)
        setIsEditAssetModalOpen(false)
    }

    const getCurrentAssetClass = () => {
        const selectedClassName = assetClassSelectRef.current?.value;

        if (!selectedClassName)
            return editingTagRef.current?.AssetClass;

        return facility?.AssetClasses?.find(o => o.Name === selectedClassName) ?? editingTagRef.current?.AssetClass;
    }

    const getCurrentTagText = () => {
        const inputElement = (comboBoxRef.current as any)?.inputElement as HTMLInputElement | undefined;
        return inputElement?.value ?? editingTagRef.current?.Text ?? '';
    }

    const getCurrentEditingTag = () => {
        const currentTag = editingTagRef.current;

        if (!currentTag)
            return null;

        return {
            ...currentTag,
            AssetClass: getCurrentAssetClass(),
            Text: getCurrentTagText()
        };
    }

    const setValidationState = (tagText: string) => {
        const hasTagText = tagText.trim().length > 0;

        setValidation(previousValidation => ({
            ...previousValidation,
            Text: {
                ...(previousValidation?.Text ?? {}),
                invalidInput: false,
                invalidValue: !hasTagText,
                invalidValueMessage: hasTagText ? undefined : CommonMessages.Validation.Required
            }
        }));

        return hasTagText;
    }

    const updateTagText = (value: string) => {
        let didChange = false;

        setEditingTag(previousTag => {
            if (!previousTag)
                return previousTag;

            if (previousTag.Text === value)
                return previousTag;

            didChange = true;
            return { ...previousTag, Text: value };
        });

        setValidationState(value);

        if (didChange)
            setHasAssetTagChange(true);
    }

    const syncCurrentEditingTag = () => {
        const currentTag = getCurrentEditingTag();

        if (!currentTag)
            return null;

        editingTagRef.current = currentTag;
        setEditingTag(currentTag);
        setValidationState(currentTag.Text ?? '');

        return currentTag;
    }

    const isValidAssetForm = (tag: IAssetTag) => {
        return setValidationState(tag?.Text ?? '');
    }


    const getViewsNamesWithTags = (array: IAssetTag[]) => {
        const uniqueByViewName = array.reduce((acc, current) => {
            const viewName = current.View?.Name;
            if (viewName && !acc.map.has(viewName)) {
                acc.map.set(viewName, true);
                acc.result.push(current);
            }
            return acc;
        }, { map: new Map(), result: [] }).result;

        //sorting the array by name
        uniqueByViewName.sort((a, b) => {
            if (a.View.Name < b.View.Name) {
                return -1;
            }
            if (a.View.Name > b.View.Name) {
                return 1;
            }
            return 0;
        });

        let linkedtags: string[] = [];
        let url = window.location.href;
        let facilityRoute = url.split('?')[0];
        uniqueByViewName.forEach((item) => {
            let link = `<div class="tag-link text-left"><a class="text-underline" target="_blank" href="${facilityRoute}?viewId=${item.View360ID}&assetId=${item.ID}">${item.View.Name}</a></div>`
            linkedtags.push(link)
        })
        return Array.from(linkedtags).join(' ');
    };

    const getOldAssetTagName = (id: string) => {
        let oldName = '';
        assetTags.forEach((asset) => {
            let checkExistTag = asset.Tags.find((tag) => tag.ID === id);
            if (checkExistTag) {
                oldName = checkExistTag.Text;
            }
        });
        return oldName;
    }
    const checkValidAssetTagRules = async (tag: IAssetTag): Promise<{ ok: boolean, actionConfirmed: boolean, AssetID?: string }> => {
        let assetClassID = tag.AssetClass.ID;
        let assetNameLowercase = tag.Text.toLocaleLowerCase();

        // Wrap the asynchronous logic in a Promise
        return new Promise((resolve, reject) => {
            // if editing mode
            if (tag.ID) {
                let existTag: IAssetTag = null;
                let existAsset: IAssetWithTags = null;
                let oldAsset: IAssetWithTags = null;
                assetTags.forEach((asset) => {
                    let checkExistTag = asset.Tags.find((assetTag) => assetTag.ID === tag.ID);
                    if (checkExistTag) {
                        existTag = checkExistTag;
                        oldAsset = asset;
                    }
                });
                existAsset = assetTags.find((asset) => asset.Identity.toLocaleLowerCase() === assetNameLowercase && asset.ID !== tag.AssetID);

                // If asset name already exists
                if (existAsset) {
                    // Check if it's a V-Plant asset with the same class
                    if (existAsset.Source == AssetSourceType.VPlant && existAsset.AssetClass.ID === assetClassID) {
                        if (existAsset.Tags.length > 0) {
                            if (oldAsset.Tags.length <= 1) {
                                //If current tag is the only tag associated to the Asset Identity delete old one
                                let oldName = getOldAssetTagName(tag.ID)
                                let views = getViewsNamesWithTags(existAsset.Tags)
                                let message = InovxLoader.t('facility:message.confirmAssociateTagAndDeleteAsset', { tagName: tag.Text, views, assetName: existAsset.Identity, oldName });

                                const messageObject = {
                                    message: message,
                                    messageHasTags: true,
                                    confirmCallback: async () => {
                                        resolve({ ok: true, actionConfirmed: true, AssetID: existAsset.ID });
                                    },
                                    cancelCallback: () => {
                                    },
                                };
                                SystemActions.displayYesNoMessage(messageObject);
                            }
                            else if (existAsset.Tags.length >= 1) {
                                // If same name associated with multiple Asset tags, show confirmation
                                let views = getViewsNamesWithTags(existAsset.Tags)
                                let message = InovxLoader.t('facility:message.confirmAssociateTag', { tagName: tag.Text, views, assetName: existAsset.Identity });

                                const messageObject = {
                                    message: message,
                                    messageHasTags: true,
                                    confirmCallback: async () => {
                                        resolve({ ok: true, actionConfirmed: true, AssetID: existAsset.ID });
                                    },
                                    cancelCallback: () => {
                                    },
                                };
                                SystemActions.displayYesNoMessage(messageObject);
                            }
                        } else {
                            //If single tag accosiate with Asset Tag
                            resolve({ ok: true, actionConfirmed: false, AssetID: existAsset.ID });
                        }

                    } else {
                        // If not V-Plant asset or different class
                        let message = InovxLoader.t('facility:message.renameAssetTagNotAllowed', { existingClass: existAsset.AssetClass.Name, assetName: existAsset.Identity, tagClass: tag.AssetClass.Name });
                        const messageObject = {
                            message: message,
                            messageHasTags: true,
                            confirmCallback: async () => {
                                resolve({ ok: false, actionConfirmed: false });
                            }
                        };
                        SystemActions.displayWarningMessage(messageObject);

                        reject(false);
                    }

                } else {
                    if (existTag) {
                        if (oldAsset.Tags.length > 1) {
                            // If same name associated with multiple Asset tags, show confirmation
                            let views = getViewsNamesWithTags(oldAsset.Tags)
                            let oldName = getOldAssetTagName(tag.ID)
                            let message = InovxLoader.t('facility:message.confirmRenameAssetTag', { views, oldName, newName: tag.Text });

                            const messageObject = {
                                message: message,
                                messageHasTags: true,
                                confirmCallback: async () => {
                                    resolve({ ok: true, actionConfirmed: true });
                                },
                                cancelCallback: () => {
                                },
                            };
                            SystemActions.displayYesNoMessage(messageObject);
                        }
                        else {
                            resolve({ ok: true, actionConfirmed: false });
                        }
                    }
                    else {
                        resolve({ ok: true, actionConfirmed: false });
                    }
                }
            } else { // if creation mode
                let existTag: IAssetTag = null;
                let existAsset: IAssetWithTags = null;
                assetTags.forEach((asset) => {
                    let checkExistTag = asset.Tags.find((assetTag) => {
                        if (assetTag.Text.toLocaleLowerCase() === assetNameLowercase) {
                            if (asset.Source == AssetSourceType.VPlant)
                                return true;
                            else
                                return (asset.AssetClassID == assetClassID);
                        }
                        return false;
                    });
                    if (checkExistTag) {
                        existTag = checkExistTag;
                        existAsset = asset;
                    }
                });

                if (existTag) {
                    // If asset tag has same class
                    if (existTag.AssetClass.ID === assetClassID) {
                        let views = getViewsNamesWithTags(existAsset.Tags)
                        let message = InovxLoader.t('facility:message.confirmSameAssetTag', { views, tagName: tag.Text });

                        const messageObject = {
                            message: message,
                            messageHasTags: true,
                            confirmCallback: async () => {
                                resolve({ ok: true, actionConfirmed: true });
                            },
                            cancelCallback: () => {
                            },
                        };
                        SystemActions.displayYesNoMessage(messageObject);
                    } else {
                        // If asset tag has different class
                        let message = InovxLoader.t('facility:message.createDuplicateAssetTagNotAllowed', { existingClass: existTag.AssetClass.Name, tagName: existTag.Text, tagClass: tag.AssetClass.Name });
                        const messageObject = {
                            message: message,
                            messageHasTags: true,
                            confirmCallback: async () => {
                                resolve({ ok: false, actionConfirmed: false });
                            }
                        };
                        SystemActions.displayWarningMessage(messageObject);

                        reject(false);
                    }
                } else {
                    resolve({ ok: true, actionConfirmed: false });
                }
            }
        });
    };

    const saveAssetTag = async () => {
        const currentTag = syncCurrentEditingTag();

        if (!currentTag)
            return;

        if (isValidAssetForm(currentTag)) {
            const checkResult = await checkValidAssetTagRules(currentTag);
            if (checkResult.ok) {
                if (checkResult.AssetID) {
                    currentTag.AssetID = checkResult.AssetID;
                }
                await AssetActions.editAssetTag(currentTag, checkResult.actionConfirmed);
                ViewerActions.editAssetTag(null);
                setHasAssetTagChange(false);
                setIsEditAssetModalOpen(false)
                ViewerActions.selectAnnotation(currentTag.ID)
            }
        } else {
            forceUpdate();
        }
    }

    const createAssetTag = async () => {
        const currentTag = syncCurrentEditingTag();

        if (!currentTag)
            return;

        if (isValidAssetForm(currentTag)) {
            const checkResult = await checkValidAssetTagRules(currentTag);

            if (checkResult.ok) {
                const tagID = await ViewerActions.createAssetTag(currentTag);
                ViewerActions.editAssetTag(null);
                setHasAssetTagChange(false);
                setIsEditAssetModalOpen(false)
            }
        } else {
            forceUpdate();
        }
    }

    const saveAssetTagAction = useAsyncActionLock(saveAssetTag);
    const createAssetTagAction = useAsyncActionLock(createAssetTag);

    const onTagTextChange = (value: string) => {
        updateTagText(value);
    }

    const onAssetClassChange = (value: string) => {
        const newClass = facility.AssetClasses.find(o => o.Name === value);

        setEditingTag(previousTag => {
            if (!previousTag || !newClass)
                return previousTag;

            if (previousTag.AssetClass?.ID === newClass.ID)
                return previousTag;

            return { ...previousTag, AssetClass: newClass, Text: '' };
        });
        setHasAssetTagChange(true);
    }

    const header = () => {
        return (<div>
            <div className="e-icon-settings dlg-template">{editingTag?.ID ? InovxLoader.t("facility:message.updateAssetTagModalTitle") : InovxLoader.t("facility:message.createAssetTag")}</div>
        </div>);
    }

    const footerTemplate = () => {
        const isSaving = saveAssetTagAction.isInFlight || createAssetTagAction.isInFlight;
        const disabledCls = isFormEnabled() && !isSaving ? '' : 'disabled';
        return (<div>
            <div className="d-flex j-cc pb-10px">
                <div>
                    {editingTag?.ID
                        ? <a id="facilityApp-save-btn" aria-disabled={isSaving} onClick={isSaving ? undefined : () => void saveAssetTagAction.run()} className={`btn btn-primary ${disabledCls}`}>{InovxLoader.t("facility:preferences.saveButton")}</a>
                        : <a id="facilityApp-save-btn" aria-disabled={isSaving} onClick={isSaving ? undefined : () => void createAssetTagAction.run()} className={`btn btn-primary ${disabledCls}`}>{InovxLoader.t("facility:action.create")}</a>}
                </div>
                <div className="ml-1">
                    <a id="facilityApp-cancel-btn" aria-disabled={isSaving} onClick={isSaving ? undefined : () => close()} className={`btn btn-default${isSaving ? ' disabled' : ''}`}>{InovxLoader.t("facility:preferences.cancelButton")}</a>
                </div>
            </div>
        </div>);
    }

    const dropData: string[] = facility?.AssetClasses?.map(object => object.Name).sort();
    var inputObj = {
        className: 'form-control',
        type: 'text'
    }
    var nameInput = jQuery.extend({
        id: 'text-input',
        name: 'Name',
        value: editingTag?.Text
    }, inputObj);

    const isFormEnabled = () => {
        const asset = FacilityStore.getAssetById(editingTag?.AssetID);
        return (!asset || asset.Source === AssetSourceType.VPlant);
    }

    const isClassDropdownEnabled = () => {
        if (!isFormEnabled())
            return false;

        let assetWithTags = assetTags.find((asset) => asset.ID === editingTag?.AssetID);
        return (assetWithTags && assetWithTags.Tags.length > 1) ? false : true;
    }

    const isTagNameEnabled = () => {
        return isFormEnabled();
    }

    const updateAssetIdentities = () => {
        var dropNames: string[] = facility?.Assets?.filter(asset => asset.AssetClassID === editingTag?.AssetClass?.ID).filter(asset => facility?.AssetTags.every(tag => tag.AssetID != asset.ID)).map(object => object.Identity).sort() ?? [];
        setAssetIdentities(prevArray => {
            // First clear the array, then set it to the new array
            return [...dropNames];
        });

    }

    const handleOpen = (e: any) => {
        if (e.popup.element.querySelectorAll('li').length === 0) {
            e.cancel = true; // Cancel opening the dropdown if dataSource is empty
        }
    };

    const handleTagFiltering = (e: FilteringEventArgs) => {
        const tagText = e.text ?? '';

        if (!tagText) {
            e.updateData(assetIdentities);
            return;
        }

        e.updateData(assetIdentities.filter(identity => identity.toLowerCase().includes(tagText.toLowerCase())));
    }

    const isValidTagName = (editingTag?.Text?.trim().length ?? 0) > 0;
    const hideDropIcon = (assetIdentities?.length === 0) ? true : false;
    return (isEditAssetModalOpen && editingTag &&
        <DialogComponent cssClass="tag-bar-modal" className="tag-bar-modal2" width='350px'
            header={header}
            footerTemplate={footerTemplate}
            showCloseIcon={true}
            visible={true}
            close={() => close()}
            isModal={true}
            zIndex={110000}
            closeOnEscape={false}
            beforeClose={handleBeforeClose}
        >
            <div className="dialogContent">
                <InovxControls.FormField title={InovxLoader.t("facility:grid.assetClass")} inputName='AssetClass'>
                    <select ref={assetClassSelectRef} disabled={!isClassDropdownEnabled()} className="form-control kb-select" name="AssetClass" title={InovxLoader.t("facility:grid.assetClass")} onChange={(e) => onAssetClassChange(e.target.value)} value={editingTag?.AssetClass?.Name}>
                        {dropData.map((item: any, key: number) => <option key={'AssetClass_' + key} value={item}>{item}</option>)}
                    </select>
                </InovxControls.FormField>
                <InovxControls.FormField title={`${InovxLoader.t("facility:grid.tagName")}*`} inputName='Text' className="mt-1">
                    <ComboBoxComponent className="form-control" popupHeight="250px"
                        autofill={true}
                        ref={comboBoxRef}
                        dataSource={assetIdentities}
                        cssClass={`${isValidTagName ? '' : 'error-border'} ${hideDropIcon ? 'hide-drop-icon' : ''}`}
                        change={() => updateTagText(getCurrentTagText())}
                        allowFiltering={true}
                        allowCustom={true}
                        filtering={handleTagFiltering}
                        filterType='Contains'
                        open={handleOpen}
                        value={editingTag?.Text}
                        enabled={isTagNameEnabled()}
                    />
                </InovxControls.FormField>
            </div>
        </DialogComponent>);

});

export default AssetTagsForm;
